import React, { ReactNode, useState } from 'react';
import { useQueryClient } from 'react-query';
import { Button, IconButton } from '@fiverr-private/button';
import { PlusIcon } from '@fiverr-private/visuals';
import { BriefCreationDrawerWrapper } from '@fiverr-private/dm_brief_creation';
import { getMiraRecruiterEntry } from '@fiverr-private/dm_brief_creation_triggers';
import { getContext } from '@fiverr-private/fiverr_context';
import { raiseToast } from '@fiverr-private/toast';
import { isInExperiment, EXPERIMENTS } from '@fiverr-private/briefs_experiments_utils';
import { PAGE_NAME } from '../../../../configuration/constants';
import { AppI18n, appTranslate } from '../../i18n';
import { useInitialProps } from '../../context';
import { BRIEFS_PAGINATED_LIST_QUERY_KEY } from '../shared/const';
import { reportButtonClick } from '../shared/bqEvents';
import { createAgenticBriefConversation, greetAgenticBriefConversation } from './utils';

interface CreateBriefButtonProps {
  title?: ReactNode;
  isIconButton?: boolean;
}

export const CreateBriefButton = ({ title, isIconButton = false }: CreateBriefButtonProps) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isCreatingConversation, setIsCreatingConversation] = useState(false);
  const queryClient = useQueryClient();
  const { currency, rollouts } = useInitialProps();
  const createBriefCopy = rollouts?.inCrowsPythonsAgenticBriefs
    ? 'lobby.create_new_brief_mira'
    : 'lobby.create_new_brief';
  const enableScreeningQuestions = Boolean(rollouts?.inLynxesScreeningQuestionsBriefsLobby);

  const startAgenticConversation = async () => {
    setIsCreatingConversation(true);

    try {
      const { briefId, conversationId } = await createAgenticBriefConversation();

      try {
        await greetAgenticBriefConversation(conversationId);
      } catch {
        // greeting is best-effort — never block brief creation
      }

      window.location.assign(`/briefs/${briefId}/composer`);
    } catch {
      setIsCreatingConversation(false);
      raiseToast({
        message: appTranslate('something_went_wrong'),
        type: 'error',
        location: 'top',
      });
    }
  };

  const onCreateClick = () => {
    reportButtonClick('create_brief');

    const { abTests, userId } = getContext();

    // Agentic gate FIRST: requires rollout AND ab-test allocation, so only users
    // in the `crows_agentic_job_post` test enter the agentic composer flow. The
    // MIRA rollout is open for everyone on dev — checking it first would make
    // the agentic flow unreachable for testers.
    const isAgenticFlow =
      Boolean(rollouts?.inCrowsAgenticJobPost) && isInExperiment(EXPERIMENTS.CROWS_AGENTIC_JOB_POST, abTests);

    if (isAgenticFlow) {
      void startAgenticConversation();
      return;
    }

    // MIRA gate SECOND: buyer in `crows_pythons_agentic_briefs` → open the Recruiter
    // agentic entry in a NEW TAB (the lobby stays put). Host picked by environment
    // (prod vs dev). Fails open — not in rollout / not logged in ⇒ existing lobby
    // flow unchanged.
    if (rollouts?.inCrowsPythonsAgenticBriefs && userId) {
      getMiraRecruiterEntry({ source: PAGE_NAME, buyer: String(userId) }).openMiraTab();
      return;
    }

    setIsDrawerOpen(true);
  };

  const onBriefSuccess = () => {
    queryClient.invalidateQueries({ queryKey: BRIEFS_PAGINATED_LIST_QUERY_KEY });
  };
  return (
    <>
      {isIconButton ? (
        <IconButton
          intent="primary"
          aria-label={appTranslate(createBriefCopy) as string}
          onClick={onCreateClick}
          loading={isCreatingConversation}
        >
          <PlusIcon />
        </IconButton>
      ) : (
        <Button onClick={onCreateClick} loading={isCreatingConversation}>
          {title || <AppI18n k={createBriefCopy} />}
        </Button>
      )}
      <BriefCreationDrawerWrapper
        onSuccess={onBriefSuccess}
        isOpen={isDrawerOpen}
        setIsOpen={setIsDrawerOpen}
        source={PAGE_NAME}
        currency={currency}
        rollouts={rollouts}
        enableEnhancedUx
        enableScreeningQuestions={enableScreeningQuestions}
      />
    </>
  );
};
